// src/components/shared/CollapsibleSection.js
//
// A DetailSection whose body can be collapsed by clicking its header.
//
// Open/closed state is remembered per section in localStorage, so a rep who
// collapses "Past Jobs" keeps it collapsed as they move between leads. Every
// section defaults to open, so a first-time viewer sees exactly what they saw
// before.
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { DetailSection } from '../styled/DetailComponents';

const STORAGE_PREFIX = 'leadDetail.section.';

// localStorage can throw outright (private mode, site data blocked), so every
// access is guarded and falls back to "open".
const readCollapsed = (id) => {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + id) === 'collapsed';
  } catch (e) {
    return false;
  }
};

const writeCollapsed = (id, collapsed) => {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + id, collapsed ? 'collapsed' : 'open');
  } catch (e) {
    /* preference is a nicety — never break the page over it */
  }
};

const Header = styled.button`
  /* Reset the button chrome; this has to look exactly like the old <h3>. */
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0 0 ${p => (p.$collapsed ? '0' : p.theme.spacing(2))} 0;
  width: 100%;
  cursor: pointer;
  font: inherit;
  text-align: left;

  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing(1)};
  color: ${p => p.theme.colors.primary};
  font-size: 1.17em;
  font-weight: bold;

  .section-title {
    display: flex;
    align-items: center;
    gap: ${p => p.theme.spacing(1)};
    min-width: 0;
  }

  .section-count {
    font-size: 0.72em;
    font-weight: 600;
    opacity: 0.65;
  }

  /* Pushes the chevron to the far right. */
  .section-chevron {
    margin-left: auto;
    font-size: 0.72em;
    opacity: 0.75;
    flex-shrink: 0;
  }

  &:hover .section-chevron {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${p => p.theme.colors.primary};
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const CollapsibleSection = ({ id, title, icon, count, defaultCollapsed = false, children }) => {
  const [collapsed, setCollapsed] = useState(() =>
    (id ? readCollapsed(id) : false) || (id ? false : defaultCollapsed)
  );

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      if (id) writeCollapsed(id, next);
      return next;
    });
  }, [id]);

  return (
    <DetailSection>
      <Header
        type="button"
        onClick={toggle}
        $collapsed={collapsed}
        aria-expanded={!collapsed}
        title={collapsed ? 'Show section' : 'Hide section'}
      >
        <span className="section-title">
          {icon}
          {title}
          {count !== undefined && count !== null && (
            <span className="section-count">({count})</span>
          )}
        </span>
        {collapsed ? <FaChevronRight className="section-chevron" /> : <FaChevronDown className="section-chevron" />}
      </Header>
      {/* Unmounted rather than hidden: DetailSection is a flex column with a
          gap, so a hidden child would still leave a blank strip behind. */}
      {!collapsed && children}
    </DetailSection>
  );
};

export default CollapsibleSection;
